import { Badge, Tooltip } from '@chakra-ui/react';
import { faHourglass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { REVIEW_RECOMMENDATION_MAP } from '@publishing-engine/domain/utils';
import {
  ADJUDICATION_STATUS_MAP,
  ASSESSMENT_RECOMMENDATION_MAP,
  ASSESSMENT_STATUS_MAP,
  BOARDMEMBER_INVITATION_MAP,
  DECISION_STATUS_MAP,
  REVIEW_STATUS_MAP,
  SUBMISSION_STATUS_MAP
} from 'src/constants';

export const BaseBadge = ({
  icon,
  variant,
  label,
  tooltipText
}: {
  icon: any;
  variant: string;
  label: string;
  tooltipText?: string;
}) =>
  tooltipText ? (
    <Tooltip shouldWrapChildren hasArrow label={tooltipText} placement="top">
      <Badge
        size="context"
        variant={variant}
        alignItems="center"
        isTruncated
        maxWidth="100%"
      >
        <FontAwesomeIcon icon={icon || faHourglass} /> {label}
      </Badge>
    </Tooltip>
  ) : (
    <Badge
      size="context"
      variant={variant}
      isTruncated
      maxWidth="100%"
      alignItems="center"
    >
      <FontAwesomeIcon icon={icon || faHourglass} /> {label}
    </Badge>
  );

const REVIEWER_TOOLTIP_MAP: Record<string, string> = {
  invited: 'You have been invited to review this submission.',
  awaitingRevisionReviewRequest:
    'You completed a review in a previous round. You may be reinvited for this round.',
  declinedWithReason:
    'You declined this review and provided additional information.',
  expired: 'Your review invitation has expired.',
  extensionRequested: 'You have requested a deadline extension.'
};

interface ContextualReviewBadgeProps {
  reviewState: string;
  context: 'editor' | 'reviewer';
}

export const ContextualReviewBadge: React.FC<ContextualReviewBadgeProps> = ({
  reviewState,
  context
}) => {
  const badgeConfig = REVIEW_STATUS_MAP[reviewState];

  if (!badgeConfig) {
    return null;
  }

  // Use reviewer-specific tooltip if available and default tooltip if not
  const tooltipText =
    context === 'reviewer' && REVIEWER_TOOLTIP_MAP[reviewState]
      ? REVIEWER_TOOLTIP_MAP[reviewState]
      : badgeConfig.tooltipText;

  return <BaseBadge {...badgeConfig} tooltipText={tooltipText} />;
};

export const recommendationBadges = Object.entries(
  REVIEW_RECOMMENDATION_MAP
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);

export const assessmentBadges = Object.entries(
  ASSESSMENT_RECOMMENDATION_MAP
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);

export const submissionStatusBadges = Object.entries(
  SUBMISSION_STATUS_MAP
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);

export const reviewStatusBadges = Object.entries(REVIEW_STATUS_MAP).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);

export const BoardMemberInvitationStatusBadges = Object.entries(
  BOARDMEMBER_INVITATION_MAP
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);

export const assessmentStatusBadges = Object.entries(
  ASSESSMENT_STATUS_MAP
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);

export const adjudicationStatusBadges = Object.entries(
  ADJUDICATION_STATUS_MAP
).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);

export const decisionStatusBadges = Object.entries(DECISION_STATUS_MAP).reduce(
  (acc, [key, value]) => ({
    ...acc,
    [key]: <BaseBadge {...value} />
  }),
  {} as Record<string, JSX.Element>
);
